import { useCallback, useContext, useMemo, useState } from "react";

import {
  FormType,
  convertCountryCodeToCountryName,
  Emptyable,
  CountryCode,
  PaymentMethod,
  FrontendRoutes,
  nameValidationRegex,
  parseFormBackgroundTheme,
  resolveBackgroundThemeCategory,
} from "@simplyk/common";
import { useElements } from "@stripe/react-stripe-js";
import { inferRouterOutputs } from "@trpc/server";
import { useSearchParams } from "next/navigation";
import { FieldErrors, UseFormHandleSubmit, UseFormSetValue, UseFormTrigger } from "react-hook-form";

import { validateFulfillmentCheckoutForSubmit } from "../PaymentElement/helper";
import { usePaymentErrorContext } from "../PaymentElement/PaymentErrorContext";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { sendEmbedLoadingMessage } from "@/canvas/features/FormV2/helpers/dynamicEmbedMessaging";
import { getFormAddOnAnalytics } from "@/canvas/features/FormV2/helpers/splitCartQuantities";
import { getAnimationNumber, TIME_BETWEEN_STEPS } from "@/canvas/features/ThankYouLoader/helpers";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { SessionContext } from "@/common/contexts/SessionContext";
import { isDonationForm } from "@/common/helpers/form";
import { pushGtmCommandSubmittedSuccessfully } from "@/common/helpers/gtm";
import { getErrorMessages } from "@/common/helpers/reactHookForm";
import { useMembershipRebuy } from "@/canvas/features/FormV2/hooks/useMembershipRebuy";
import { getThankYouPageUrl, navigateAfterPayment } from "@/common/helpers/thankYouPage";
import { runWithMinimumDuration } from "@/common/helpers/time";
import { AppRouterType } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useDurationOnPage } from "@/common/hooks/useDurationOnPage";
import { useIsEmbeddedModal } from "@/common/hooks/useEmbeddedModal";
import { useFormQueryParams } from "@/common/hooks/useFormParams";
import { useRegistrationRedirect } from "@/common/hooks/useRegistrationRedirect";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { DiscountOutput, DonationFormFrontendPageOutput, TicketingFrontendPageOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useSubmitCommand } from "@/components/PaymentProcessor/hooks/useSubmitCommand";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { CustomRedirect, Event, isPaymentFailed, PaymentSucceeded } from "@/components/PaymentProcessor/type";

export const formObjectIsTicketing = (
  formObject: TicketingFrontendPageOutput | DonationFormFrontendPageOutput,
  formType: FormType
): formObject is TicketingFrontendPageOutput => {
  return formType === FormType.Ticketing;
};

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export interface FreePaymentProcessorProps {
  handleSubmit: UseFormHandleSubmit<TicketingPaymentInput | DonationFormPaymentInput>;
  setValue: UseFormSetValue<TicketingPaymentInput | DonationFormPaymentInput>;
  trigger: UseFormTrigger<TicketingPaymentInput | DonationFormPaymentInput>;
  formId: string;
  label: string;
  stripeProductId: Emptyable<string>;
  validDiscount: DiscountOutput | null;
  formObject: TicketingFrontendPageOutput | DonationFormFrontendPageOutput;
  formType: FormType;
  event?: Event;
  errors: FieldErrors;
  customRedirect?: CustomRedirect;
}

export const useFreePaymentSubmit = (props: FreePaymentProcessorProps) => {
  const { handleSubmit } = props;
  const elements = useElements();

  const {
    formObject: ticketing,
    hasADate,
    commandFulfillment,
    addOnRates,
    ticketsPurchased,
  } = useFormV2Context(FormType.Ticketing);
  const getMembershipRebuy = useMembershipRebuy();
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const { paymentError, handlePaymentError } = usePaymentErrorContext();
  const { impactChainUtmSourceEmail, source } = useFormQueryParams();

  const { logAmplitudeEvent } = useAmplitude();

  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const isEmbeddedModal = useIsEmbeddedModal(query);
  const { t } = useTranslate();
  const { handleSubmitCommand } = useSubmitCommand(props);
  const { getDurationTimeOnPage } = useDurationOnPage();

  const {
    commandId,
    organization,
    postTransactionUrl,
    tip,
    selectedPaymentMethod,
    isEmbed,
    isEmbedV2,
    formEmbedVersion,
    embedId,
    category,
    formType,
    formData,
    selectedTip,
    displayedFormAmount,
    setIsSubmitting,
    generateETicket,
    formAmplitudeContext,
    themeColor,
    displayAddressQuestion,
  } = useContext(FrontendFormContext);
  const { locale, isoLocale } = useLocaleContext();
  const { sessionId } = useContext(SessionContext);
  const { shouldRedirectToPeerToPeer, getRegistrationRedirectUrl } = useRegistrationRedirect();

  const handlePaymentFailed = useCallback(
    ({ errors, caughtError }: { errors?: string[]; caughtError?: unknown; params?: Record<string, unknown> }) => {
      const rawErrorMessage = errors?.[0] || t("common", "unknownError", { code: 100 });
      const caughtErrorCode =
        caughtError !== undefined
          ? `caught_exception – ${caughtError instanceof Error ? caughtError.message : String(caughtError)}`
          : "free_payment_unknown_error";
      handlePaymentError({
        rawErrorCode: errors?.[0] ? "free_payment_error" : caughtErrorCode,
        rawErrorMessage,
        step: "server-submit",
        paymentMethod: PaymentMethod.Free,
        isExpressCheckout: false,
        isKnownError: Boolean(errors?.[0]),
      });
      setPaymentInProgress(false);
    },
    [handlePaymentError, t]
  );

  const {
    availableAddOnsCount,
    itemsCount: cartItemsCount,
    addOnsCount: cartAddOnsCount,
  } = getFormAddOnAnalytics(addOnRates, ticketsPurchased);

  const amplitudePayload = useMemo(
    () => ({
      ...formAmplitudeContext,
      formType: category,
      form_has_addons: availableAddOnsCount > 0,
      available_addons_count: availableAddOnsCount,
      items_count: cartItemsCount,
      addons_count: cartAddOnsCount,
      source,
      countCustomQuestions: props.formObject.questions?.length,
      displayAddressQuestion,
      encourageMonthlyDonation: isDonationForm(formData) && Boolean(formData.encourageMonthlyDonations),
      hasALinkedTicketing: isDonationForm(formData) && Boolean(formData.redirectUrl),
      hasAdditionalDonation: Boolean(ticketing.extraDonation),
      hasBanner: Boolean(props.formObject.bannerUrl),
      hasBannerVideo: Boolean(props.formObject.bannerVideoUrl),
      hasCheckOption: props.formObject.chequeMinimumAmount !== null,
      hasInHonourOption: isDonationForm(formData) && Boolean(formData.allowInHonour),
      hasLogo: Boolean(props.formObject.logoUrl),
      hasNoSuggestedAmount: formAmplitudeContext.hasNoSuggestedAmount || null,
      hasReceipt: isDonationForm(formData) && formData.hasReceipt,
      hasReceiptMessage: formAmplitudeContext.hasReceiptMessage || null,
      hasTarget: Boolean(ticketing.thermometerTarget),
      isEmbed,
      isPrimaryCampaign: isDonationForm(formData) && formData.isPrimaryCampaign,
      isPrimaryTeam: isDonationForm(formData) && formData.isPrimaryTeam,
      isoLocale,
      locale,
      organizationCountry: organization?.country,
      tipAmount: tip,
      tipChoice: selectedTip.percentage,
      tipPercentage: displayedFormAmount > 0 ? ((tip / displayedFormAmount) * 100).toFixed(2) : "N A",
      formId: props.formObject.id,
      amount: displayedFormAmount,
      paymentMethod: selectedPaymentMethod,
      isExpressCheckout: false,
      impact_chain: impactChainUtmSourceEmail ? true : false,
      is_dark_mode: props.formObject.isDarkMode || false,
      background_category: resolveBackgroundThemeCategory(parseFormBackgroundTheme(props.formObject.backgroundTheme)),
      form_created_at: props.formObject.createdAtUtc,
      title_font_style: props.formObject.titleFontStyle || null,
      has_communication_preferences: Boolean(props.formObject.communicationPreferences?.length),
    }),
    [
      category,
      source,
      displayedFormAmount,
      formAmplitudeContext,
      formData,
      impactChainUtmSourceEmail,
      isEmbed,
      isoLocale,
      locale,
      organization?.country,
      props.formObject.chequeMinimumAmount,
      props.formObject.bannerUrl,
      props.formObject.bannerVideoUrl,
      displayAddressQuestion,
      props.formObject.id,
      props.formObject.isDarkMode,
      props.formObject.backgroundTheme,
      props.formObject.createdAtUtc,
      props.formObject.titleFontStyle,
      props.formObject.logoUrl,
      props.formObject.questions?.length,
      props.formObject.communicationPreferences?.length,
      selectedPaymentMethod,
      selectedTip.percentage,
      ticketing.extraDonation,
      ticketing.thermometerTarget,
      tip,
      availableAddOnsCount,
      cartItemsCount,
      cartAddOnsCount,
    ]
  );

  const handleFormSubmit = handleSubmit(async (paymentInput: TicketingPaymentInput | DonationFormPaymentInput) => {
    setPaymentInProgress(true);
    setIsSubmitting(true);
    const minimumDuration = TIME_BETWEEN_STEPS * 1000 * getAnimationNumber({ isFree: true, hasADate });

    const url = await runWithMinimumDuration<Emptyable<string>>(async () => {
      logAmplitudeEvent(AmplitudeEvents.ConfirmFreePayment);

      const session = {
        sessionId,
        durationOnPage: getDurationTimeOnPage(),
      };

      const addressElement = elements?.getElement?.("address");
      const addressValue = await addressElement?.getValue?.();
      if (addressValue) {
        // Validate that the Stripe Address Element is complete before proceeding
        if (!addressValue.complete) {
          await elements?.submit();
          setPaymentInProgress(false);
          setIsSubmitting(false);
          return { callbackResult: null, shouldAwaitMinimumDuration: false };
        }

        const { city, country, line1, line2, postal_code, state } = addressValue.value.address;
        paymentInput.address = line2 ? `${line1} ${line2}` : line1;
        paymentInput.city = city;
        paymentInput.postalCode = postal_code;
        paymentInput.region = state;
        paymentInput.country = convertCountryCodeToCountryName(country as CountryCode);
        paymentInput.firstName = addressValue.value.firstName;
        paymentInput.lastName = addressValue.value.lastName;

        props.setValue("firstName", addressValue.value.firstName);
        props.setValue("lastName", addressValue.value.lastName);

        const isFirstNameValid =
          !addressValue.value.firstName || nameValidationRegex.test(addressValue.value.firstName);
        const isLastNameValid = !addressValue.value.lastName || nameValidationRegex.test(addressValue.value.lastName);

        if (!isFirstNameValid || !isLastNameValid) {
          await handlePaymentFailed({ errors: [t("common", "name.invalidCharacterUsed")] });
          return { callbackResult: null, shouldAwaitMinimumDuration: false };
        }
      }

      props.setValue("country", paymentInput.country);
      props.setValue("region", paymentInput.region);
      if (!(await props.trigger("raffleEligibility"))) {
        return { callbackResult: null, shouldAwaitMinimumDuration: false };
      }

      if (
        validateFulfillmentCheckoutForSubmit({
          paymentInput,
          formType: props.formType,
          t,
          handlePaymentError,
          selectedPaymentMethod: PaymentMethod.Free,
          isExpressCheckout: false,
          commandId,
          requireFulfillmentCheckout: commandFulfillment?.showFulfillmentOnPayment ?? false,
          canOfferSameAsBilling: displayAddressQuestion,
          offeredMethods: commandFulfillment?.offeredMethods ?? [],
        })
      ) {
        return { callbackResult: null, shouldAwaitMinimumDuration: false };
      }

      const submitCommandInput: SubmitCommandFormInput = {
        paymentInput,
        paymentMethod: PaymentMethod.Free,
        formObject: props.formObject,
        user: null,
        contact: null,
        formType: props.formType,
        validDiscount: props.validDiscount,
        stripePaymentMethodId: null,
        session,
        routerQuery: query,
        commandId,
        locale,
        tip: 0,
        tipPercentage: 0,
        formEmbedVersion: formEmbedVersion ?? undefined,
      };

      try {
        const result = await handleSubmitCommand(submitCommandInput);
        if (isPaymentFailed(result)) {
          await handlePaymentFailed({ errors: [result.error.message] });
          logAmplitudeEvent(AmplitudeEvents.DonorFormSubmitted, {
            ...amplitudePayload,
            errors: result.error,
          });
          return { callbackResult: null, shouldAwaitMinimumDuration: false };
        } else {
          const command = result.data?.command;
          pushGtmCommandSubmittedSuccessfully({
            organization_id: organization?.id ?? "",
            form_id: formData.id,
            form_type: formType,
            form_category: category,
            amount: 0,
            extra_donation: 0,
            currency: command?.currency ?? undefined,
            transaction_id: result.data?.transaction?.id ?? "",
          });
          const url = await handlePaymentFinalized(result, paymentInput);
          logAmplitudeEvent(AmplitudeEvents.DonorFormSubmitted, amplitudePayload);
          return { callbackResult: url, shouldAwaitMinimumDuration: true };
        }
      } catch (thrownError) {
        await handlePaymentFailed({ caughtError: thrownError });
        const thrownErrorMessage = thrownError instanceof Error ? thrownError.message : String(thrownError);
        logAmplitudeEvent(AmplitudeEvents.DonorFormSubmitted, {
          ...amplitudePayload,
          errors: thrownErrorMessage,
          rawErrorCode: `caught_exception – ${thrownErrorMessage}`,
        });
      }
      return { callbackResult: null, shouldAwaitMinimumDuration: false };
    }, minimumDuration).finally(() => {
      setPaymentInProgress(false);
      setIsSubmitting(false);
    });
    if (url) {
      if (isEmbedV2 && url.includes(FrontendRoutes.EmbedThankYou)) {
        sendEmbedLoadingMessage();
      }
      await navigateAfterPayment(url);
    }
  });

  const handlePaymentFinalized = useCallback(
    async (result: PaymentSucceeded, paymentInput: TicketingPaymentInput | DonationFormPaymentInput) => {
      const command = result.data?.command;
      const emailUpdateToken = result.data?.emailUpdateToken;
      const buyerTicketToken = result.data?.buyerTicketToken;
      const formType = props.formType;

      // Redirect to P2P campaign page if this is a registration purchase
      if (shouldRedirectToPeerToPeer(paymentInput, formType)) {
        const redirectUrl = getRegistrationRedirectUrl({
          firstName: command?.firstName,
          lastName: command?.lastName,
          email: command?.email,
          commandId: command?.id,
        });
        if (redirectUrl) {
          return redirectUrl;
        }
        return null;
      }

      if (result.onSuccess) {
        await result.onSuccess();
        return null;
      }

      const event = props.event;

      return getThankYouPageUrl({
        command: command as NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"],
        formType,
        organization,
        postTransactionUrl,
        isEmbeddedModal,
        generateETicket,
        event,
        formData,
        isoLocale,
        category,
        isAuction: false,
        themeColor,
        isDarkMode: props.formObject.isDarkMode || false,
        backgroundTheme: parseFormBackgroundTheme(props.formObject.backgroundTheme),
        isEmbed,
        embedId: embedId ?? undefined,
        emailUpdateToken,
        buyerTicketToken,
        membershipRebuy: getMembershipRebuy(command, paymentInput),
      });
    },
    [
      props.formType,
      props.event,
      props.formObject.isDarkMode,
      props.formObject.backgroundTheme,
      shouldRedirectToPeerToPeer,
      getRegistrationRedirectUrl,
      organization,
      postTransactionUrl,
      isEmbeddedModal,
      generateETicket,
      formData,
      isoLocale,
      category,
      themeColor,
      isEmbed,
      embedId,
      getMembershipRebuy,
    ]
  );

  const onSubmit = useCallback(
    async (event?: React.MouseEvent) => {
      event && event.preventDefault();
      // Form is submitted, but can be invalid
      await handleFormSubmit();

      if (Object.keys(props.errors).length > 0) {
        logAmplitudeEvent(AmplitudeEvents.DonorFormSubmitted, {
          ...amplitudePayload,
          errors: getErrorMessages({
            keys: Object.keys(props.errors),
            errors: props.errors,
          }),
        });
      }
    },
    [amplitudePayload, handleFormSubmit, logAmplitudeEvent, props.errors]
  );

  return { onSubmit, paymentError, paymentInProgress };
};
